/**
 * Onboarding Store - 2025 Pinia Best Practices
 *
 * Architecture:
 * - Composition API for better TypeScript inference
 * - Pinia persist for automatic localStorage sync
 * - toRefs() for reactive state exposure (not readonly - per Pinia docs)
 * - Strong TypeScript typing throughout
 * - Convention: State should only be mutated through actions
 * - Coordination with wizard store via test-flow
 *
 * @see https://pinia.vuejs.org/core-concepts/
 */

import { defineStore } from 'pinia'
import { computed, reactive, toRefs } from 'vue'
import {
  OnboardingDataSchema,
  OnboardingValidationError,
  type OnboardingData
} from '@monorepo/types'

// ===========================
// TYPES
// ===========================

/**
 * All types are imported from @monorepo/types (single source of truth)
 * - OnboardingData: Complete data structure
 * - OnboardingDataSchema: Zod validation schema
 * - OnboardingValidationResult: Validation result type
 * - OnboardingValidationError: Validation error structure
 */

/**
 * Onboarding store state (for coordination)
 */
export interface OnboardingState {
  formData: OnboardingData
  currentStep: number
  isCompleted: boolean
  errors: OnboardingValidationError[]
  touchedFields: string[]  // Changed from Set to Array (JSON serializable)
  wasAutoResumed: boolean
  skipSummary: boolean // Feature Flag: Skip summary step (Step 5)
}

// ===========================
// STORE DEFINITION
// ===========================

export const useOnboardingStore = defineStore('onboarding', () => {
  // ===========================
  // STATE
  // ===========================

  /**
   * Main form data - using reactive() for complex object
   * Benefits: Better performance for nested data, cleaner access patterns
   */
  const state = reactive<OnboardingState>({
    formData: {
      childName: '',
      childAge: 0,
      childSchool: '',
      childSituation: '',
      childInterests: '',
      childGender: 'other',
      challenges: [],
      parentFeelings: [],
      selectedGoals: [],
      startedAt: new Date().toISOString()
    },
    currentStep: 1,
    isCompleted: false,
    errors: [],
    touchedFields: [],
    wasAutoResumed: false,
    skipSummary: true // Enable skip by default
  })

  // ===========================
  // COMPUTED (GETTERS)
  // ===========================

  /**
   * Is form data valid (comprehensive Zod validation)
   */
  const isValid = computed<boolean>(() => {
    const result = OnboardingDataSchema.safeParse(state.formData)
    return result.success
  })

  /**
   * Progress percentage for current step
   */
  const completionPercentage = computed<number>(() => {
    const totalSteps = 5
    return Math.round((state.currentStep / totalSteps) * 100)
  })

  /**
   * Can user proceed to next step
   */
  const canProceed = computed<boolean>(() => validateCurrentStep())

  /**
   * Has any onboarding data been entered
   */
  const hasAnyData = computed<boolean>(() => {
    return !!(
      state.formData.childName ||
      state.formData.childAge ||
      state.currentStep > 1
    )
  })

  // ===========================
  // VALIDATION HELPERS
  // ===========================

  /**
   * Validate a single field using Zod schema
   */
  function validateField(field: keyof OnboardingData): boolean {
    // Remove existing errors for this field
    state.errors = state.errors.filter(error => error.field !== field)

    // Create a partial schema for just this field
    const fieldSchema = OnboardingDataSchema.pick({ [field]: true })

    // Validate just this field
    const result = fieldSchema.safeParse({ [field]: state.formData[field] })

    if (!result.success) {
      // Extract error message from Zod
      const error = result.error.issues[0]
      state.errors.push({
        field,
        message: error?.message || 'Invalid value'
      })
      return false
    }

    return true
  }

  /**
   * Validate current step fields
   */
  function validateCurrentStep(): boolean {
    let isStepValid = true

    switch (state.currentStep) {
      case 1: // Child Introduction
        isStepValid = validateField('childName') &&
                     validateField('childAge') &&
                     validateField('childSchool') &&
                     validateField('childSituation')
        break

      case 2: // Challenges
        isStepValid = (state.formData.challenges?.length ?? 0) > 0
        if (!isStepValid) {
          state.errors = state.errors.filter(e => e.field !== 'challenges')
          state.errors.push({ field: 'challenges', message: 'Bitte wählen Sie mindestens eine Herausforderung' })
        }
        break

      case 3: // Parent Feelings
        isStepValid = (state.formData.parentFeelings?.length ?? 0) > 0
        if (!isStepValid) {
          state.errors = state.errors.filter(e => e.field !== 'parentFeelings')
          state.errors.push({ field: 'parentFeelings', message: 'Bitte wählen Sie mindestens ein Gefühl aus' })
        }
        break

      case 4: // Goals
        isStepValid = (state.formData.selectedGoals?.length ?? 0) > 0
        if (!isStepValid) {
          state.errors = state.errors.filter(e => e.field !== 'selectedGoals')
          state.errors.push({ field: 'selectedGoals', message: 'Bitte wählen Sie mindestens ein Ziel' })
        }
        break

      case 5: // Summary - always valid
        isStepValid = true
        break

      default:
        isStepValid = true
    }

    return isStepValid
  }

  /**
   * Validate all fields
   */
  function validateAllFields(): boolean {
    const fieldsToValidate: (keyof OnboardingData)[] = [
      'childName', 'childAge', 'childSchool', 'childSituation',
      'challenges', 'parentFeelings', 'selectedGoals'
    ]

    let allValid = true
    fieldsToValidate.forEach(field => {
      const isFieldValid = validateField(field)
      if (!isFieldValid) allValid = false
    })

    return allValid
  }

  /**
   * Get validation errors for a specific field
   */
  function getFieldError(field: keyof OnboardingData): string | null {
    const error = state.errors.find(e => e.field === field)
    return error?.message || null
  }

  /**
   * Check if field has been touched
   */
  function isFieldTouched(field: keyof OnboardingData): boolean {
    return state.touchedFields.includes(field)
  }

  /**
   * Should show error for a field (touched + has error)
   */
  function shouldShowFieldError(field: keyof OnboardingData): boolean {
    return isFieldTouched(field) && getFieldError(field) !== null
  }

  // ===========================
  // ACTIONS
  // ===========================

  /**
   * Update a single form field
   */
  function updateField(field: keyof OnboardingData, value: unknown): void {
    (state.formData as unknown as Record<string, unknown>)[field as string] = value
    if (!state.touchedFields.includes(field)) {
      state.touchedFields.push(field)
    }
    validateField(field)
  }

  /**
   * Update challenges selection (handles exclusive 'none' option)
   */
  function updateChallenges(challengeId: string, selected: boolean): void {
    const challenges = state.formData.challenges || []
    if (selected) {
      // Handle 'none' option - clear others when selected
      if (challengeId === 'none') {
        state.formData.challenges = ['none']
      } else {
        // Remove 'none' if other challenges are selected
        const filtered = challenges.filter(id => id !== 'none')
        if (!filtered.includes(challengeId)) {
          filtered.push(challengeId)
        }
        state.formData.challenges = filtered
      }
    } else {
      state.formData.challenges = challenges.filter(id => id !== challengeId)
    }
    if (!state.touchedFields.includes('challenges')) {
      state.touchedFields.push('challenges')
    }
    validateField('challenges')
  }

  /**
   * Update parent feelings selection
   */
  function updateParentFeelings(feelingId: string, selected: boolean): void {
    const feelings = state.formData.parentFeelings || []
    if (selected) {
      if (!feelings.includes(feelingId)) {
        feelings.push(feelingId)
      }
    } else {
      const index = feelings.indexOf(feelingId)
      if (index !== -1) {
        feelings.splice(index, 1)
      }
    }
    state.formData.parentFeelings = feelings

    if (!state.touchedFields.includes('parentFeelings')) {
      state.touchedFields.push('parentFeelings')
    }
    validateField('parentFeelings')
  }

  /**
   * Update goals selection (max 3)
   */
  function updateGoals(goalId: string, selected: boolean): void {
    const goals = state.formData.selectedGoals || []
    if (selected) {
      if (!goals.includes(goalId)) {
        // Limit to 3 selections
        if (goals.length < 3) {
          goals.push(goalId)
          state.formData.selectedGoals = goals
        }
      }
    } else {
      state.formData.selectedGoals = goals.filter(id => id !== goalId)
    }
    if (!state.touchedFields.includes('selectedGoals')) {
      state.touchedFields.push('selectedGoals')
    }
    validateField('selectedGoals')
  }

  /**
   * Navigate to next step (with validation)
   */
  function nextStep(): boolean {
    // Mark all required fields for current step as touched
    markCurrentStepFieldsAsTouched()

    if (validateCurrentStep()) {
      if (state.currentStep < 5) {
        state.currentStep++
        return true
      }
    }
    return false
  }

  /**
   * Navigate to previous step
   */
  function previousStep(): boolean {
    if (state.currentStep > 1) {
      state.currentStep--
      return true
    }
    return false
  }

  /**
   * Mark current step fields as touched
   */
  function markCurrentStepFieldsAsTouched(): void {
    switch (state.currentStep) {
      case 1:
        if (!state.touchedFields.includes('childName')) state.touchedFields.push('childName')
        if (!state.touchedFields.includes('childAge')) state.touchedFields.push('childAge')
        if (!state.touchedFields.includes('childSchool')) state.touchedFields.push('childSchool')
        if (!state.touchedFields.includes('childSituation')) state.touchedFields.push('childSituation')
        break
      case 2:
        if (!state.touchedFields.includes('challenges')) state.touchedFields.push('challenges')
        break
      case 3:
        if (!state.touchedFields.includes('parentFeelings')) state.touchedFields.push('parentFeelings')
        break
      case 4:
        if (!state.touchedFields.includes('selectedGoals')) state.touchedFields.push('selectedGoals')
        break
    }
  }

  /**
   * Jump to specific step
   */
  function goToStep(step: number): boolean {
    if (step >= 1 && step <= 5) {
      state.currentStep = step
      return true
    }
    return false
  }

  /**
   * Complete onboarding and return data
   */
  function complete(): OnboardingData {
    if (validateAllFields()) {
      state.formData.completedAt = new Date().toISOString()
      state.isCompleted = true
      console.log('✅ Onboarding completed successfully')
      return { ...state.formData }
    }
    throw new Error('Onboarding data is not valid')
  }

  /**
   * Reset onboarding to initial state
   */
  function reset(): void {
    state.formData = {
      childName: '',
      childAge: 0,
      childSchool: '',
      childSituation: '',
      childInterests: '',
      childGender: 'other',
      challenges: [],
      parentFeelings: [],
      selectedGoals: [],
      startedAt: new Date().toISOString()
    }
    state.currentStep = 1
    state.isCompleted = false
    state.errors = []
    state.touchedFields = []
    state.wasAutoResumed = false

    console.log('✅ Onboarding reset successfully')
  }

  // ===========================
  // RETURN PUBLIC API
  // ===========================

  /**
   * Using toRefs() to maintain reactivity (Pinia best practice)
   * Note: We use toRefs() instead of readonly() because:
   * 1. Pinia docs warn readonly() breaks SSR, devtools, and plugins
   * 2. Convention over enforcement - state should only be mutated through actions
   * 3. toRefs() maintains proper reactivity for destructuring
   *
   * @see https://pinia.vuejs.org/core-concepts/#setup-stores
   */
  return {
    // State (reactive refs via toRefs - maintains reactivity)
    ...toRefs(state),

    // Computed (getters)
    isValid,
    completionPercentage,
    canProceed,
    hasAnyData,

    // Actions (always mutable)
    updateField,
    updateChallenges,
    updateParentFeelings,
    updateGoals,
    validateField,
    validateCurrentStep,
    validateAllFields,
    nextStep,
    previousStep,
    goToStep,
    complete,
    reset,
    markCurrentStepFieldsAsTouched,

    // Helper methods
    getFieldError,
    isFieldTouched,
    shouldShowFieldError
  }
}, {
  // ===========================
  // PINIA PERSIST CONFIGURATION
  // ===========================
  persist: {
    key: 'ulrich-onboarding',
    storage: localStorage
  }
})

/**
 * Type export for coordination layer
 */
export type OnboardingStore = ReturnType<typeof useOnboardingStore>
